/**
 * When the app reports to Sentry, under which environment name, and which
 * errors are never sent (board D75). Plain functions with no imports, so a
 * test can run them without the Sentry SDK; shared/lib/sentry.ts applies them.
 *
 * @module sentryRules
 */

/**
 * Errors that are not faults and are never sent. Each carries its reason.
 *
 * - FedCM abort or network error (Sentry BLOOM-4P and 4Q, B24): Google One
 *   Tap's prompt is cancelled when the sign-in page goes away after a
 *   sign-in, or cannot fetch its token, and Google logs it with console.error.
 * - Firebase auth/invalid-credential (BLOOM-14): a wrong password; the
 *   gardener is told on the sign-in screen.
 * - Vite "[hmr] Failed to reload": a local dev-server hot reload failing; no
 *   gardener ever sees it.
 *
 * @kept K1
 */
export const KNOWN_NOISE: RegExp[] = [
  /FedCM get\(\) rejects with (AbortError|NetworkError)/,
  /auth\/invalid-credential/,
  /\[hmr\] Failed to reload/,
];

/** What the app's build knows, passed in so the rules stay testable. */
export interface SentryBuildInfo {
  /** VITE_SENTRY_DSN. */
  dsn?: string;
  /** True in a production build (import.meta.env.PROD). */
  isProductionBuild: boolean;
  /** VITE_SENTRY_LOCAL: "1" or "true" opts a local run in. */
  localOptIn?: string;
  /** VITE_SENTRY_ENVIRONMENT, for a real hosted site (for example "staging"). */
  environment?: string;
}

/**
 * Whether the app should report, and under which environment.
 *
 * On only with a DSN and either a production build or the local opt-in
 * (VITE_SENTRY_LOCAL). A production build reports as VITE_SENTRY_ENVIRONMENT
 * ("staging" on a real staging site) or "production"; a local opted-in run
 * reports as "local".
 *
 * @param info - What the build knows.
 * @returns Whether to start Sentry, and its environment name.
 */
export function sentryBuildPlan(info: SentryBuildInfo): { enabled: boolean; environment: string } {
  const dsn = (info.dsn ?? '').trim();
  const opt = (info.localOptIn ?? '').trim().toLowerCase();
  const optedIn = opt === '1' || opt === 'true';
  const enabled = dsn !== '' && (info.isProductionBuild || optedIn);
  const environment = info.isProductionBuild ? (info.environment ?? '').trim() || 'production' : 'local';
  return { enabled, environment };
}

/**
 * Whether an error message is known noise (KNOWN_NOISE).
 *
 * @param message - The error text.
 * @returns True when it should not be sent.
 */
export function isKnownNoise(message: string): boolean {
  return KNOWN_NOISE.some((re) => re.test(message));
}
